import { useMemo, useState } from "react";

import { styled } from "../../styles";
import { DualAxisChart } from "./DualAxisChart";
import { DualAxisOptionsBar } from "./DualAxisOptionsBar";
import {
  COMPARABLE_MEASUREMENTS,
  DualAxisDataPoint,
  DualAxisSeries,
  MOCK_CFDNA_DATA,
  MOCK_FEV1_DATA,
  MOCK_SERIES,
  getMockData,
} from "./mockData";
import { SpirometryDetailsPoc } from "./SpirometryDetailsPoc";

const Container = styled("div", {
  display: "flex",
  height: "100%",
});

const MeasurementContainer = styled("div", {
  height: "100%",
  width: "100%",
  minWidth: "900px",
  padding: "24px 0 0 0",
  position: "relative",
});

const ExperimentalTag = styled("div", {
  position: "absolute",
  top: "16px",
  right: "48px",
  padding: "4px 12px",
  borderRadius: "4px",
  backgroundColor: "#FFF3E0",
  color: "#E65100",
  fontSize: "11px",
  fontFamily: "$openSans",
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: "0.3px",
});

const HeaderContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  marginLeft: "40px",
  marginBottom: "28px",
});

const Title = styled("h1", {
  fontWeight: 500,
  fontSize: "$text-xl",
  lineHeight: "22px",
  color: "$black",
});

const Subtitle = styled("h2", {
  marginTop: "4px",
  marginBottom: "12px",
  color: "$gray-80",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
});

const LegendContainer = styled("div", {
  width: "100%",
  minHeight: "100px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  position: "relative",
  bottom: "-16px",
});

const MainValuesContainer = styled("div", {
  display: "flex",
  flexDirection: "row",
  minWidth: "363px",
});

const MainValueContainer = styled("div", {
  minWidth: "73px",
  display: "flex",
  flexDirection: "column",
  marginLeft: "48px",
});

const NumberLabel = styled("div", {
  color: "#666",
  fontFamily: "$openSans",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "22px",
});

const TextLabel = styled("div", {
  color: "#666",
  fontFamily: "$openSans",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  marginTop: "2px",
});

const DateLabel = styled("div", {
  color: "#939393",
  fontFamily: "$openSans",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  marginTop: "5px",
});

const Divider = styled("div", {
  width: "88px",
  height: "1px",
  transform: "rotate(90deg)",
  flexShrink: 0,
  borderRadius: "0.5px",
  background: "#E6E6E6",
});

const SeriesLegendContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingLeft: "12px",
});

const SeriesLegendItem = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: "$openSans",
  fontSize: "12px",
  color: "#666",
});

function computeStats(data: DualAxisDataPoint[], key: string) {
  const values = data
    .map((d) => d[key])
    .filter((v): v is number => typeof v === "number");

  if (values.length === 0)
    return {
      highest: "-",
      lowest: "-",
      mean: "-",
      highestDate: "-",
      lowestDate: "-",
    };

  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  const maxEntry = data.find((d) => d[key] === maxVal);
  const minEntry = data.find((d) => d[key] === minVal);

  return {
    highest: maxVal.toFixed(1),
    lowest: minVal.toFixed(1),
    mean: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
    highestDate: maxEntry?.time ?? "-",
    lowestDate: minEntry?.time ?? "-",
  };
}

function buildComparedSeries(comparedKeys: string[]): {
  series: DualAxisSeries[];
  dataSets: DualAxisDataPoint[][];
} {
  const series: DualAxisSeries[] = comparedKeys.map((key) => {
    const measurement = COMPARABLE_MEASUREMENTS.find((m) => m.key === key);
    return {
      yAxisId: "right" as const,
      yKey: key,
      label: `${measurement?.label ?? key}, Mock Device`,
      unit: measurement?.unit ?? "",
      color: measurement?.color ?? "#999",
      dotShape: measurement?.dotShape ?? "circle",
      lineOpacity: 0.7,
      strokeDasharray: "6 4",
      lineJointType: "linear" as const,
    };
  });

  const dataSets: DualAxisDataPoint[][] = comparedKeys.map((key) =>
    getMockData(key)
  );

  return { series, dataSets };
}

type DualAxisChartModalProps = {
  subtitle?: string;
};

type DualAxisChartLegendProps = {
  series?: DualAxisSeries[];
  dataSets?: DualAxisDataPoint[][];
};

export function DualAxisChartModal({
  subtitle = "Jun 2025 - Apr 2026",
}: DualAxisChartModalProps) {
  const [comparedKeys, setComparedKeys] = useState<string[]>(["cf_dna"]);
  const [selectedPointTime, setSelectedPointTime] = useState<string | null>(
    null
  );

  const MAX_COMPARED = 3;

  // The unit is locked to the first selected measurement's unit.
  // Only measurements with the same unit can be added.
  const lockedUnit = useMemo(() => {
    if (comparedKeys.length === 0) return null;
    const first = COMPARABLE_MEASUREMENTS.find(
      (m) => m.key === comparedKeys[0]
    );
    return first?.unit ?? null;
  }, [comparedKeys]);

  const handleToggleCompare = (key: string) => {
    setComparedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_COMPARED) return prev;
      return [...prev, key];
    });
  };

  const { allSeries, allDataSets, chartTitle } = useMemo(() => {
    const baseSeries: DualAxisSeries = {
      yAxisId: "left",
      yKey: "fev1",
      label: "FEV1, MIR Spirobank Smart",
      unit: "L",
      color: "#C47E2B",
      dotShape: "circle",
      lineOpacity: 1,
    };

    const compared = buildComparedSeries(comparedKeys);

    const rightUnit = compared.series.length > 0 ? compared.series[0].unit : "";
    const comparedNames = compared.series
      .map((s) => {
        const m = COMPARABLE_MEASUREMENTS.find((cm) => cm.key === s.yKey);
        return m?.label ?? s.yKey;
      })
      .join(", ");

    let title = "FEV1 (L)";
    if (comparedNames && rightUnit) {
      title = `FEV1 (L), ${comparedNames} (${rightUnit})`;
    }

    return {
      allSeries: [baseSeries, ...compared.series],
      allDataSets: [MOCK_FEV1_DATA, ...compared.dataSets],
      chartTitle: title,
      rightUnit,
    };
  }, [comparedKeys]);

  return (
    <Container>
      <MeasurementContainer>
        <ExperimentalTag>Experimental</ExperimentalTag>
        <HeaderContainer>
          <Title>{chartTitle}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <DualAxisOptionsBar
            comparedKeys={comparedKeys}
            lockedUnit={lockedUnit}
            onToggleCompare={handleToggleCompare}
          />
        </HeaderContainer>

        <DualAxisChart
          variant="modal"
          series={allSeries}
          dataSets={allDataSets}
          onPointClick={(point) => setSelectedPointTime(point?.time ?? null)}
        />
      </MeasurementContainer>
      <SpirometryDetailsPoc selectedPointTime={selectedPointTime} />
    </Container>
  );
}

export function DualAxisChartLegend({
  series = MOCK_SERIES,
  dataSets = [MOCK_FEV1_DATA, MOCK_CFDNA_DATA],
}: DualAxisChartLegendProps) {
  const primaryStats = computeStats(dataSets[0] ?? [], series[0]?.yKey ?? "");
  const primaryLabel = series[0]?.yKey?.toUpperCase() ?? "";

  return (
    <LegendContainer>
      <MainValuesContainer>
        <MainValueContainer>
          <NumberLabel>{primaryStats.highest}</NumberLabel>
          <TextLabel>{primaryLabel} highest</TextLabel>
          <DateLabel>{primaryStats.highestDate}</DateLabel>
        </MainValueContainer>

        <MainValueContainer>
          <NumberLabel>{primaryStats.lowest}</NumberLabel>
          <TextLabel>{primaryLabel} lowest</TextLabel>
          <DateLabel>{primaryStats.lowestDate}</DateLabel>
        </MainValueContainer>

        <MainValueContainer>
          <NumberLabel>{primaryStats.mean}</NumberLabel>
          <TextLabel>{primaryLabel} mean</TextLabel>
          <DateLabel>&mdash;</DateLabel>
        </MainValueContainer>
      </MainValuesContainer>

      <Divider />

      <SeriesLegendContainer>
        {series.map((s) => (
          <SeriesLegendItem key={s.yKey}>
            <svg width={12} height={12}>
              <circle cx={6} cy={6} r={5} fill={s.color} />
            </svg>
            <span>{s.label}</span>
          </SeriesLegendItem>
        ))}
      </SeriesLegendContainer>
    </LegendContainer>
  );
}
